const STORAGE_KEY = 'sib-document-chunked-upload-sessions';

export type PersistedChunkedUploadSession = {
  organizationId: string;
  filename: string;
  filesize: number;
  md5Hash: string;
  uploadId: string;
  blockSize: number;
  completedBlockIndices: number[];
  createdAt: number;
};

export const buildChunkedUploadSessionKey = ({
  organizationId,
  filename,
  filesize,
  md5Hash,
}: Pick<
  PersistedChunkedUploadSession,
  'organizationId' | 'filename' | 'filesize' | 'md5Hash'
>): string => `${organizationId}:${filename}:${filesize}:${md5Hash}`;

const readSessions = (): Record<string, PersistedChunkedUploadSession> => {
  if (typeof sessionStorage === 'undefined') return {};

  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);

    if (!raw) return {};

    const parsed = JSON.parse(raw) as Record<
      string,
      PersistedChunkedUploadSession
    >;

    return parsed ?? {};
  } catch {
    return {};
  }
};

const writeSessions = (
  sessions: Record<string, PersistedChunkedUploadSession>
): void => {
  if (typeof sessionStorage === 'undefined') return;

  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
};

export const loadPersistedChunkedUploadSession = (
  sessionKey: string
): PersistedChunkedUploadSession | null => readSessions()[sessionKey] ?? null;

export const savePersistedChunkedUploadSession = (
  sessionKey: string,
  session: PersistedChunkedUploadSession
): void => {
  writeSessions({
    ...readSessions(),
    [sessionKey]: {
      ...session,
      createdAt: session.createdAt ?? Date.now(),
    },
  });
};

export const markPersistedChunkedUploadBlockComplete = (
  sessionKey: string,
  blockIndex: number
): void => {
  const sessions = readSessions();
  const session = sessions[sessionKey];

  if (!session || session.completedBlockIndices.includes(blockIndex)) return;

  sessions[sessionKey] = {
    ...session,
    completedBlockIndices: [...session.completedBlockIndices, blockIndex].sort(
      (left, right) => left - right
    ),
  };

  writeSessions(sessions);
};

export const clearPersistedChunkedUploadSession = (
  sessionKey: string
): void => {
  const sessions = readSessions();

  if (!(sessionKey in sessions)) return;

  delete sessions[sessionKey];
  writeSessions(sessions);
};

export const resetPersistedChunkedUploadBlockProgress = (
  sessionKey: string
): void => {
  const sessions = readSessions();
  const session = sessions[sessionKey];

  if (!session) return;

  sessions[sessionKey] = {
    ...session,
    completedBlockIndices: [],
  };

  writeSessions(sessions);
};

export const pruneStaleUploadSessions = (ttlMs: number): void => {
  const sessions = readSessions();
  const cutoff = Date.now() - ttlMs;
  let changed = false;

  for (const [sessionKey, session] of Object.entries(sessions)) {
    const createdAt = session.createdAt ?? 0;

    if (createdAt < cutoff) {
      delete sessions[sessionKey];
      changed = true;
    }
  }

  if (changed) {
    writeSessions(sessions);
  }
};
